import React, { createContext, useContext } from 'react';
import { Box } from '@mantine/core';
import clsx from 'clsx';
import { TABLE_ZOOM_DEFAULT, useTableZoom } from '../hooks/useTableZoom';

type PageZoomContextValue = ReturnType<typeof useTableZoom>;

const PageZoomContext = createContext<PageZoomContextValue | null>(null);

type PageZoomProviderProps = {
    children: React.ReactNode;
};

export function PageZoomProvider({ children }: PageZoomProviderProps) {
    const zoom = useTableZoom();

    return (
        <PageZoomContext.Provider value={zoom}>
            {children}
        </PageZoomContext.Provider>
    );
}

export function usePageZoom(): PageZoomContextValue {
    const context = useContext(PageZoomContext);

    if (!context) {
        throw new Error('usePageZoom must be used within PageZoomProvider');
    }

    return context;
}

export function usePageZoomOptional(): PageZoomContextValue | null {
    return useContext(PageZoomContext);
}

type PageZoomHostProps = {
    children: React.ReactNode;
    className?: string;
};

export function PageZoomHost({ children, className }: PageZoomHostProps) {
    const { zoomLevel } = usePageZoom();
    const isZoomed = zoomLevel !== TABLE_ZOOM_DEFAULT;

    return (
        <Box className={clsx('flex-1 min-h-0 overflow-hidden', className)}>
            <Box
                className={clsx(
                    'module-page-zoom-host',
                    isZoomed && 'module-page-zoom-host--active',
                )}
                style={
                    isZoomed
                        ? ({
                              '--module-page-zoom': zoomLevel,
                          } as React.CSSProperties)
                        : undefined
                }
            >
                {children}
            </Box>
        </Box>
    );
}
